/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "../../../components/button.component";
import { Card } from "../../../components/card.component";
import { PatientList } from "../../../features/patient/patientList.component";
import { AppRouteApi } from "../../../config/appRoute";
import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../core/redux/ReduxStore";
import { ActionsPatientTable } from "../../../core/redux/slices/slicePatientTable";
import { ExportButton } from "../../../components/export.component";
import { SelectPickerInput } from "../../../components/textInput.component";
import { Form } from "rsuite";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../../hooks";
import { ApiSchemaFilteredPatient } from "../../../models/api";
import { EnumPatientStatus } from "../../../libs";
import { CustomDatePicker } from "../../../components";
import { ApiPatient } from "../../../core/apis";
import { StorageAuth } from "../../../core/localStorage";

export const PagePatientView = () => {
  const dispatch = useAppDispatch();
  const {
    pageNumber,
    pageSize,
    searchKey,
    status: patientStatus,
    nextPageToken,
    startDate,
    endDate,
    shouldRefetch,
  } = useAppSelector((state) => state.patientTable);
  const { data: allPatientData, isValidating: isLoadingPatientDropdown } =
    ApiPatient.GetAllFromCompany.Hook(StorageAuth.CompanyId);

  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      NextPageToken: nextPageToken,
      PageSize: pageSize,
      PageNumber: pageNumber,
      SearchKey: searchKey,
      PatientStatus: patientStatus || EnumPatientStatus.All,
      StartDate: startDate!,
      EndDate: endDate!,
    };
  }, [
    nextPageToken,
    pageSize,
    pageNumber,
    searchKey,
    patientStatus,
    startDate,
    endDate,
  ]);
  const {
    apiData: patientData,
    isFetching: isPatientDataFetching,
    refetch: refetchPatient,
  } = useTablePaginationSearchFilter<
    ApiSchemaFilteredPatient,
    typeof ActionsPatientTable
  >(ActionsPatientTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Patient.Root(),
  });

  const status = ["All", "Active", "Expired"].map((status) => ({
    label: status,
    value: status,
  }));

  const allPatientMap =
    allPatientData?.map((patient) => ({
      label: `${patient.firstName} ${patient.lastName}`,
      value: `${patient.firstName}`,
    })) ?? [];

  const resetData = useCallback(() => {
    dispatch(ActionsPatientTable.resetData());
  }, []);

  const handlePatientChange = (value: string) => {
    dispatch(ActionsPatientTable.setSearchKey(value));
    dispatch(ActionsPatientTable.goToPage(1));
  };

  useEffect(() => {
    return () => {
      resetData();
    };
  }, [resetData]);

  useEffect(() => {
    shouldRefetch && refetchPatient();
  }, [shouldRefetch, refetchPatient]);

  return (
    <Card
      header={
        <div className="row align-items-center  justify-content-between">
          <div className="col-md-8 col-12 content-box-title">
            <div>
              <p className="text-capitalize my-1 text-green-200">
                Manage Patient
              </p>
            </div>
            <div className="collapse-icon"></div>
          </div>
          <div className="col-md-4 col-12 mt-1">
            <form className="search-component d-flex justify-content-end">
              <ExportButton
                params={{ pageNumber, pageSize, searchKey }}
                component="Patient"
              />
            </form>
          </div>
        </div>
      }
    >
      <div className="mb-2 px-2 border-bottom border-1" id="filterPanel">
        {(endDate ||
          startDate ||
          (patientStatus && patientStatus !== EnumPatientStatus.All)) && (
          <div className="d-flex justify-content-end mb-1">
            <Button
              name="Reset Filter Options"
              className="btn-danger btn-sm"
              onClick={resetData}
            >
              Reset Filter Options
            </Button>
          </div>
        )}

        <Form fluid>
          <div className="row">
            <div className="col-md-3">
              <div className="row">
                <SelectPickerInput
                  fullWidth
                  data={status}
                  name="status"
                  searchable={false}
                  value={patientStatus}
                  renderEmpty={
                    <p className="text-muted p-3">No Data Available</p>
                  }
                  onChange={(value) => {
                    dispatch(ActionsPatientTable.goToPage(1));
                    dispatch(ActionsPatientTable.setStatus(value));
                  }}
                >
                  Filter By Patient Status
                </SelectPickerInput>
              </div>
            </div>
            <div className="col-md-3">
              <div className="row">
                <SelectPickerInput
                  fullWidth
                  data={allPatientMap}
                  name="patientName"
                  disabled={isLoadingPatientDropdown}
                  renderEmpty={
                    <p className="text-muted p-3">No Data Available</p>
                  }
                  onChange={(value) => {
                    handlePatientChange(value);
                  }}
                >
                  Filter By Patient
                </SelectPickerInput>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <CustomDatePicker
                  name="start"
                  action={ActionsPatientTable}
                  value={startDate ? new Date(startDate) : null}
                >
                  Start Date
                </CustomDatePicker>
                <CustomDatePicker
                  name="end"
                  action={ActionsPatientTable}
                  value={endDate ? new Date(endDate) : null}
                >
                  End Date
                </CustomDatePicker>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <PatientList
        totalCount={patientData?.totalCount || 0}
        data={patientData?.list}
        isValidating={isPatientDataFetching}
      />
    </Card>
  );
};
